import type { DotProps } from "@/v2-backoffice-client/components/dot"

// How a customer relates to its Fenerum billing account
// (lic.CustomerAccounts) — the billing sibling of the HubSpot company link.
// Computed SERVER-side (the billingFacts APPLY in backoffice.Get_Acc_Customers
// emits these wire literals, and customersFilterRegistry compiles the column
// filter against the same CASE), so the badge, the filter and the list can
// never disagree.
export const BILLING_ACCOUNT_STATUS_LINKED = "Linked"
export const BILLING_ACCOUNT_STATUS_ARCHIVED = "Archived"
export const BILLING_ACCOUNT_STATUS_LINKABLE = "Linkable"
export const BILLING_ACCOUNT_STATUS_NOT_LINKED = "NotLinked"

type BillingAccountStatusValue =
  | typeof BILLING_ACCOUNT_STATUS_LINKED
  | typeof BILLING_ACCOUNT_STATUS_ARCHIVED
  | typeof BILLING_ACCOUNT_STATUS_LINKABLE
  | typeof BILLING_ACCOUNT_STATUS_NOT_LINKED

type BillingAccountStatusConfig = {
  label: string
  variant: DotProps["variant"]
  description: string
  // The action an operator should reach for; empty for the healthy state.
  fix?: string
}

// One label + dot color per status — the single source for the badge, the
// table's grouping/export values and the filter options.
const STATUS_CONFIG: Record<
  BillingAccountStatusValue,
  BillingAccountStatusConfig
> = {
  [BILLING_ACCOUNT_STATUS_LINKED]: {
    label: "Linked",
    variant: "success-1",
    description:
      "The customer is linked to its Fenerum billing account — subscriptions and invoices flow into the Billing tab.",
  },
  [BILLING_ACCOUNT_STATUS_ARCHIVED]: {
    label: "Archived",
    variant: "muted",
    description:
      "The linked Fenerum account is archived. The link persists and keeps mirroring, but Fenerum bills nothing on an archived account.",
    fix: "Expected for churned customers. If this customer is billed again, reactivate or recreate the account in Fenerum.",
  },
  [BILLING_ACCOUNT_STATUS_LINKABLE]: {
    label: "Linkable",
    variant: "warning",
    description:
      "An unlinked Fenerum account carries this customer's organisation number, but the link hasn't been made.",
    fix: "Usually heals on the next hourly sync. If it persists, the Billing Accounts board explains what blocks it (typically a duplicate account).",
  },
  [BILLING_ACCOUNT_STATUS_NOT_LINKED]: {
    label: "Not linked",
    variant: "muted",
    description:
      "No Fenerum billing account is linked to this customer — nothing is invoiced through Fenerum.",
    fix: "Expected for trials and prospects. A paying customer without a link needs an account in Fenerum carrying its organisation number.",
  },
}

export function getBillingAccountStatusConfig(
  status: string | null | undefined,
): BillingAccountStatusConfig | undefined {
  if (!status) return undefined
  return STATUS_CONFIG[status as BillingAccountStatusValue]
}

// Grouping / export / filter-option label for a wire status.
export function getBillingAccountStatusLabel(
  status: string | null | undefined,
): string {
  return getBillingAccountStatusConfig(status)?.label ?? ""
}

// How the customer pays, worst state governing — computed SERVER-side in
// lic.CustomerRevenueFacts (PaymentHealth CASE) from the mirrored invoice
// statuses, so the board column, its filter and the Billing tab's stat card
// can never disagree. Wire literals mirror the view.
export const PAYMENT_HEALTH_NONPAYMENT = "Nonpayment"
export const PAYMENT_HEALTH_OVERDUE = "Overdue"
export const PAYMENT_HEALTH_CURRENT = "Current"
export const PAYMENT_HEALTH_SETTLED = "Settled"

type PaymentHealthValue =
  | typeof PAYMENT_HEALTH_NONPAYMENT
  | typeof PAYMENT_HEALTH_OVERDUE
  | typeof PAYMENT_HEALTH_CURRENT
  | typeof PAYMENT_HEALTH_SETTLED

type PaymentHealthConfig = {
  label: string
  variant: DotProps["variant"]
  description: string
  // The action an operator should reach for; empty for healthy states.
  fix?: string
}

// Labels AND dot colors match the invoice-status pills (Fenerum's own
// vocabulary: open=info, due/overdue=destructive, paid=success,
// nonpayment=orange) so a customer's health reads exactly like its worst
// invoice — never a second dialect for the same state. The wire literals
// (Settled/Current) stay as the lic.CustomerRevenueFacts CASE emits them.
const PAYMENT_HEALTH_CONFIG: Record<PaymentHealthValue, PaymentHealthConfig> = {
  [PAYMENT_HEALTH_SETTLED]: {
    label: "Paid",
    variant: "success-1",
    description: "Nothing outstanding — every mirrored invoice is paid.",
  },
  [PAYMENT_HEALTH_CURRENT]: {
    label: "Open",
    variant: "info",
    description:
      "Open invoices within payment terms; nothing is past its due date.",
  },
  [PAYMENT_HEALTH_OVERDUE]: {
    label: "Overdue",
    variant: "destructive",
    description:
      "At least one invoice is past its due date and unpaid — the customer is behind on billing.",
    fix: "Fenerum's dunning is chasing it. Worth a heads-up to the customer before renewal or upsell conversations.",
  },
  [PAYMENT_HEALTH_NONPAYMENT]: {
    label: "Non-payment",
    variant: "orange",
    description:
      "Invoices were written off or sent to collection (Fenerum 'nonpayment') and the money never came in.",
    fix: "Check the account in Fenerum before extending anything — this customer has failed collection before.",
  },
}

export function getPaymentHealthConfig(
  health: string | null | undefined,
): PaymentHealthConfig | undefined {
  if (!health) return undefined
  return PAYMENT_HEALTH_CONFIG[health as PaymentHealthValue]
}

// Grouping / export / filter-option label for a wire health value.
export function getPaymentHealthLabel(
  health: string | null | undefined,
): string {
  return getPaymentHealthConfig(health)?.label ?? ""
}

// Where the customer's billed revenue over the last 12 months ranks among
// all actively billed customers — computed SERVER-side in
// lic.CustomerRevenueFacts (ValueTier CASE over RevenueRank): the #1
// customer is always VIP, then VIP = top 10 %, High = top 40 %, Mid =
// top 70 %, Low = the rest. Dormant = billed before, nothing in the last
// 12 months. Wire literals mirror the view.
export const VALUE_TIER_VIP = "VIP"
export const VALUE_TIER_HIGH = "High"
export const VALUE_TIER_MID = "Mid"
export const VALUE_TIER_LOW = "Low"
export const VALUE_TIER_DORMANT = "Dormant"

type ValueTierValue =
  | typeof VALUE_TIER_VIP
  | typeof VALUE_TIER_HIGH
  | typeof VALUE_TIER_MID
  | typeof VALUE_TIER_LOW
  | typeof VALUE_TIER_DORMANT

type ValueTierConfig = {
  label: string
  variant: DotProps["variant"]
  description: string
  // The action an operator should reach for; empty when none applies.
  fix?: string
}

const VALUE_TIER_CONFIG: Record<ValueTierValue, ValueTierConfig> = {
  [VALUE_TIER_VIP]: {
    label: "VIP",
    // The backoffice theme's amber chrome — gold, for the top of the book.
    variant: "primary",
    description:
      "Top of the book: among the top 10 % by billed revenue over the last 12 months (the #1 customer is always VIP).",
  },
  [VALUE_TIER_HIGH]: {
    label: "High",
    variant: "success-1",
    description: "Top 40 % by billed revenue over the last 12 months.",
  },
  [VALUE_TIER_MID]: {
    label: "Mid",
    variant: "secondary",
    description: "Top 70 % by billed revenue over the last 12 months.",
  },
  [VALUE_TIER_LOW]: {
    label: "Low",
    variant: "muted",
    description:
      "The remaining tail of actively billed customers by 12-month revenue.",
  },
  [VALUE_TIER_DORMANT]: {
    label: "Dormant",
    variant: "warning",
    description:
      "Billed before, but nothing in the last 12 months — revenue churn, not low value.",
    fix: "If this customer is still active in the app, licensing or billing may have quietly lapsed — check the Licenses tab and Fenerum.",
  },
}

export function getValueTierConfig(
  tier: string | null | undefined,
): ValueTierConfig | undefined {
  if (!tier) return undefined
  return VALUE_TIER_CONFIG[tier as ValueTierValue]
}

// Grouping / export / filter-option label for a wire tier value.
export function getValueTierLabel(tier: string | null | undefined): string {
  return getValueTierConfig(tier)?.label ?? ""
}
